import { Component } from 'react';
import { View, ScrollView, Text, TouchableOpacity, Image } from 'react-native';
import { Button, Popup, DocumentTitle, Loading, TextInput, LabelledView } from '@symbolic/rn-lib';
import { connect } from '@symbolic/redux';
import { resourceActions } from '~/redux';
import { api } from '@symbolic/lib';

import AdminMenu from '~/components/admin-menu';
import SecondaryHeader from '~/components/secondary-header';
import AdminSection from '~/components/admin-section';
import AceEditor from 'react-ace';

import K from '~/k';
import createIconWhite from '~/assets/create-icon-white.png';
import trashIcon from '~/assets/trash-icon.png';

import { accentColorForOrg } from '~/helpers/org-helper';

import settingsIcon from '~/assets/settings-icon.png';
import helpIcon from '~/assets/help-icon.png';
import _ from 'lodash';

class ProductPricingRuleVersion extends Component {
  state = {
    currentExpressionsValue: '',
    createdDate: '',
    isValid: true,
    hasUnsavedChanges: false,
    error: null,
    isFocused: false
  };

  componentDidMount() {
    const { versionData } = this.props;
    this.setState({
      currentExpressionsValue: JSON.stringify(versionData.expressions || [], null, 2),
      createdDate: versionData.created || ''
    });

    // Add keyboard shortcut listener for Ctrl/Cmd + S
    this.handleKeyDown = this.handleKeyDown.bind(this);
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    // Clean up keyboard event listener
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  handleKeyDown = (event) => {
    // Check for Ctrl+S (Windows/Linux) or Cmd+S (Mac)
    if ((event.ctrlKey || event.metaKey) && event.key === 's') {
      event.preventDefault(); // Prevent browser's default save dialog

      // Only save if this editor is focused, has unsaved changes, and JSON is valid
      if (this.state.isFocused && this.state.hasUnsavedChanges && this.state.isValid) {
        this.handleSave();
      }
    }
  };

  handleEditorFocus = () => {
    this.setState({ isFocused: true });
  };

  handleEditorBlur = () => {
    this.setState({ isFocused: false });
  };

  componentDidUpdate(prevProps) {
    if (prevProps.versionData !== this.props.versionData) {
      this.setState({
        currentExpressionsValue: JSON.stringify(this.props.versionData.expressions || [], null, 2),
        createdDate: this.props.versionData.created || '',
        hasUnsavedChanges: false
      });
    }
  }

  handleDateChange = (value) => {
    const originalDate = this.props.versionData.created || '';
    const hasUnsavedChanges = value !== originalDate || this.hasExpressionsChanges();

    this.setState({ createdDate: value, hasUnsavedChanges });
  };

  handleExpressionsChange = (value) => {
    const originalExpressionsValue = JSON.stringify(this.props.versionData.expressions || [], null, 2);
    const hasUnsavedChanges = value !== originalExpressionsValue || this.hasDateChanges();

    this.setState({ currentExpressionsValue: value, hasUnsavedChanges });
    this.validateJSON(value);
  };

  hasDateChanges = () => {
    const originalDate = this.props.versionData.created || '';
    return this.state.createdDate !== originalDate;
  };

  hasExpressionsChanges = () => {
    const originalExpressionsValue = JSON.stringify(this.props.versionData.expressions || [], null, 2);
    return this.state.currentExpressionsValue !== originalExpressionsValue;
  };

  validateJSON = (value) => {
    try {
      JSON.parse(value);
      this.setState({ isValid: true, error: null });
    } catch (err) {
      this.setState({
        isValid: false,
        error: `Invalid JSON: ${err.message}`
      });
    }
  };

  handleSave = () => {
    if (!this.state.isValid) return;

    try {
      const parsedExpressions = JSON.parse(this.state.currentExpressionsValue);
      const versionData = {
        created: this.state.createdDate,
        expressions: parsedExpressions
      };
      this.props.onSave(this.props.versionKey, versionData);
      this.setState({ hasUnsavedChanges: false });
    } catch (err) {
      this.setState({
        isValid: false,
        error: `Save failed: ${err.message}`
      });
    }
  };

  handleDelete = async () => {
    if (await confirm('Are you want to delete this pricing rule version?')) this.props.onDelete(this.props.versionKey);
  };

  render() {
    const { versionKey } = this.props;
    const { currentExpressionsValue, createdDate, isValid, hasUnsavedChanges, error } = this.state;

    return (
      <AdminSection title={`Version ${versionKey}`} contentStyle={{flexDirection: 'column', marginHorizontal: K.spacing}}>
        <View style={{flexDirection: 'row', alignItems: 'center', width: '100%', marginBottom: K.spacing}}>
          <Button
            onPress={this.handleSave}
            disabled={!isValid || !hasUnsavedChanges}
            label="Save"
            style={{
              opacity: !isValid || !hasUnsavedChanges ? 0.5 : 1,
            }}
          />
          {hasUnsavedChanges && (
            <Text style={{color: 'orange', marginLeft: K.spacing}}>
              Unsaved changes
            </Text>
          )}
          <View style={{flex: 1, justifyContent: 'flex-end'}}>
            <TouchableOpacity style={{alignSelf: 'flex-end'}} onPress={this.handleDelete}>
              <Image source={trashIcon} style={{...K.defaultIconSize, opacity: 0.5}} />
            </TouchableOpacity>
          </View>
        </View>
        {(!isValid) && (
          <View style={{marginBottom: K.spacing, marginLeft: K.spacing}}>
            <Text style={{color: 'red'}}>{error ? error : 'Invalid JSON'}</Text>
          </View>
        )}
        <View style={{ width: '100%', borderColor: !isValid ? 'red' : hasUnsavedChanges ? 'orange' : 'transparent', borderWidth: 1, borderRadius: K.borderRadius}}>
          <LabelledView gray label={'Effective Date (UTC)'} styles={{}}>
            <TextInput
              placeholder="YYYY-MM-DD HH:MM:SS"
              onChange={({value}) => this.handleDateChange(value)}
              value={createdDate}
            />
          </LabelledView>
          <LabelledView gray label={'Expressions'} styles={{outerView: {marginBottom: 0}, innerView: {padding: K.spacing, paddingTop: K.spacing}}}>
            <AceEditor
              mode="json"
              theme="github"
              value={currentExpressionsValue}
              onChange={this.handleExpressionsChange}
              onFocus={this.handleEditorFocus}
              onBlur={this.handleEditorBlur}
              width="100%"
              maxLines={Infinity}
              wrapEnabled={true}
              showPrintMargin={false}
              setOptions={{
                useWorker: false,
                enableBasicAutocompletion: true,
                enableLiveAutocompletion: true,
                enableSnippets: true,
                showLineNumbers: true,
                tabSize: 2,
              }}
            />
          </LabelledView>
        </View>
      </AdminSection>
    );
  }
}

class AdminProductPricingRulePage extends Component {
  state = {
    isProductPricingRuleSettingsPopupVisible: false,
    isProductPricingRuleHelpPopupVisible: false,
    image: null,
    uploading: false,
    uploadWasSuccessful: false,
    uploadHasFailed: false,
    isLoading: true
  };

  async componentDidMount() {
    var {productPricingRuleId, session: {activeOrg: {id: orgId}}} = this.props;

    var productPricingRules = await api.get('productPricingRules', {where: {id: productPricingRuleId, orgId}});

    await this.props.trackProductPricingRules({productPricingRules});

    setTimeout(() => this.setState({isLoading: false}));
  }

  toggleProductPricingRuleSettingsPopup = () => this.setState({isProductPricingRuleSettingsPopupVisible: !this.state.isProductPricingRuleSettingsPopupVisible});

  toggleProductPricingRuleHelpPopup = () => this.setState({isProductPricingRuleHelpPopupVisible: !this.state.isProductPricingRuleHelpPopupVisible});

  archive = async () => {
    this.props.updateProductPricingRule({id: this.props.productPricingRule.id, props: {deleted: this.props.productPricingRule.deleted ? 0 : 1}});
  };

  handleVersionSave = (versionKey, versionData) => {
    const updatedVersions = {
      ...this.props.productPricingRule.versions,
      [versionKey]: versionData
    };

    this.props.updateProductPricingRule({
      id: this.props.productPricingRule.id,
      props: { versions: updatedVersions }
    });
  };

  handleVersionDelete = (versionKey) => {
    const { productPricingRule } = this.props;
    const updatedVersions = { ...productPricingRule.versions };

    // Remove the version by key
    delete updatedVersions[versionKey];

    this.props.updateProductPricingRule({
      id: productPricingRule.id,
      props: { versions: updatedVersions }
    });
  };

  createProductPricingRuleVersion = async () => {
    const { productPricingRule } = this.props;
    const existingVersions = productPricingRule.versions || {};

    // Generate next version key using incremental numbers
    const existingKeys = Object.keys(existingVersions).map(key => parseInt(key)).filter(key => !isNaN(key));
    const nextVersionKey = existingKeys.length > 0 ? Math.max(...existingKeys) + 1 : 1;

    // Create timestamp for tomorrow at 00:00:00 UTC
    const tomorrow = new Date();
    tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
    tomorrow.setUTCHours(0, 0, 0, 0); // force UTC midnight

    const createdTimestamp = tomorrow.toISOString().slice(0, 19).replace('T', ' ');

    // Create new version with default content
    const newVersion = {
      created: createdTimestamp,
      expressions: []
    };

    // Update the versions object
    const updatedVersions = {
      ...existingVersions,
      [nextVersionKey.toString()]: newVersion
    };

    // Save via Redux action
    this.props.updateProductPricingRule({
      id: productPricingRule.id,
      props: { versions: updatedVersions }
    });
  };

  render() {
    if (this.state.isLoading) {
      return (
        <View style={{flex: 1, flexDirection: 'row'}}>
          <AdminMenu activeOrg={this.props.session.activeOrg} />
          <View style={{flex: 1}}>
            <Loading />
          </View>
        </View>
      );
    }
    else {
      const {productPricingRule} = this.props;

      return (
        <DocumentTitle title={`Admin: ${productPricingRule.title || productPricingRule.name || 'Untitled Rule'} - Pricing Rule`}>
          <View style={{flex: 1, flexDirection: 'row'}}>
            <AdminMenu activeOrg={this.props.session.activeOrg} />
            <View style={{flex: 1}}>
              <SecondaryHeader
                title={productPricingRule.title || productPricingRule.name || 'Untitled Rule'}
                titleIsEditable
                subTitle={`ID: ${productPricingRule.id}`}
                onTitleChange={({value: title}) => this.props.updateProductPricingRule({id: productPricingRule.id, props: {title}})}
                belowHeaderComponent={
                  <View style={{marginTop: K.spacing * 2, flexDirection: 'row', alignItems: 'center', gap: K.spacing}}>
                    <Button
                      onPress={this.toggleProductPricingRuleSettingsPopup}
                      icon={settingsIcon}
                      style={{backgroundColor: 'transparent', ...K.defaultIconSize}}
                    />
                    <Button
                      onPress={this.toggleProductPricingRuleHelpPopup}
                      icon={helpIcon}
                      style={{backgroundColor: 'transparent', ...K.defaultIconSize}}
                    />
                  </View>
                }
              />
              <ScrollView style={{flex: 1}} contentContainerStyle={{padding: K.spacing, flex: 1}}>
                {productPricingRule.versions ? (
                  _.map(
                    _.orderBy(
                      Object.entries(productPricingRule.versions),
                      ([key, version]) => version.created,
                      ['desc']
                    ),
                    ([key, versionData]) => (
                      <ProductPricingRuleVersion
                        key={key}
                        versionKey={key}
                        versionData={versionData}
                        onSave={this.handleVersionSave}
                        onDelete={this.handleVersionDelete}
                      />
                    )
                  )
                ) : (
                  <View style={{ padding: K.spacing }}>
                    <Text>No versions available for this pricing rule.</Text>
                  </View>
                )}
              </ScrollView>
              <TouchableOpacity
                onPress={this.createProductPricingRuleVersion}
                style={{position: 'fixed', bottom: K.spacing * 2, left: K.spacing * 2, backgroundColor: accentColorForOrg({org: this.props.session.activeOrg}), width: 70, height: 70, borderRadius: 70, alignItems: 'center', 'justifyContent': 'center'}}
              >
                <Image source={createIconWhite} style={{width: K.calc(35), height: K.calc(35)}}/>
              </TouchableOpacity>
              {this.state.isProductPricingRuleSettingsPopupVisible && (
                <Popup onClose={this.toggleProductPricingRuleSettingsPopup}>
                  <TextInput
                    style={{...K.fonts.pageHeader, paddingHorizontal: K.spacing, paddingVertical: K.spacing, backgroundColor: 'white', marginBottom: K.spacing}}
                    value={productPricingRule.title}
                    onChange={({value}) => this.props.updateProductPricingRule({id: productPricingRule.id, props: {title: value}})}
                    blurOnEnter
                    blurOnSubmit
                    multiline
                    standardAutoheightStyles
                    returnKeyType='done'
                  />
                  <Button
                    light
                    onPress={this.archive}
                    label={productPricingRule.deleted === 0 ? 'ARCHIVE' : 'UNARCHIVE'}
                  />
                </Popup>
              )}
              {this.state.isProductPricingRuleHelpPopupVisible && (
                <Popup onClose={this.toggleProductPricingRuleHelpPopup}>
                  <ScrollView style={{padding: K.spacing}}>
                    <Text style={{...K.fonts.pageHeader, marginBottom: K.spacing}}>Data Structure Explanation</Text>

                    <Text style={{...K.fonts.standard, marginBottom: K.spacing}}>
                      This data defines a <Text style={{fontWeight: 'bold'}}>pricing rule</Text>. Pricing rules contain <Text style={{fontWeight: 'bold'}}>versions</Text>, and each version documents a <Text style={{fontWeight: 'bold'}}>formula</Text> used to calculate a price based on the selected product and product options.
                    </Text>

                    <View style={{height: 1, backgroundColor: K.colors.gray, marginVertical: K.spacing}} />

                    <Text style={{...K.fonts.label, marginBottom: K.spacing}}>Summary</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing, marginBottom: K.spacing / 2}}>• A pricing rule contains multiple <Text style={{fontWeight: 'bold'}}>versions</Text>.</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing, marginBottom: K.spacing / 2}}>• Each version defines a <Text style={{fontWeight: 'bold'}}>formula</Text> made of nested expressions.</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing, marginBottom: K.spacing / 2}}>• Expressions are either <Text style={{fontWeight: 'bold'}}>values</Text>, <Text style={{fontWeight: 'bold'}}>property lookups</Text>, or <Text style={{fontWeight: 'bold'}}>groups of operations</Text>.</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing, marginBottom: K.spacing / 2}}>• To edit: add/update expressions, keep <Text style={{fontWeight: 'bold'}}>nesting correct</Text>, operators <Text style={{fontWeight: 'bold'}}>consistent</Text>, and <Text style={{fontWeight: 'bold'}}>descriptions clear</Text>.</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing, marginBottom: K.spacing}}>• Always use <Text style={{fontWeight: 'bold'}}>whole numbers</Text> for money (no decimals).</Text>

                    <Text style={{...K.fonts.label, marginTop: K.spacing, marginBottom: K.spacing}}>Versions</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing, marginBottom: K.spacing / 2}}>• A pricing rule has multiple <Text style={{fontWeight: 'bold'}}>versions</Text>.</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing, marginBottom: K.spacing / 2}}>• Each version contains a complete formula expressed as JSON.</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing, marginBottom: K.spacing / 2}}>• When you load a quote, the system selects the version based on the <Text style={{fontWeight: 'bold'}}>effective pricing date</Text> of the product order.</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing, marginBottom: K.spacing}}>• <Text style={{fontWeight: 'bold'}}>Best practice when updating pricing:</Text></Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing * 2, marginBottom: K.spacing / 2}}>1. Create a new version with a <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>created</Text> date <Text style={{fontWeight: 'bold'}}>in the future</Text> so existing active orders are not affected.</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing * 2, marginBottom: K.spacing}}>2. Create a test quote with an effective pricing date <Text style={{fontWeight: 'bold'}}>after</Text> the <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>created</Text> date of your new version.</Text>

                    <Text style={{...K.fonts.label, marginTop: K.spacing, marginBottom: K.spacing}}>Structure of a Version</Text>
                    <Text style={{...K.fonts.standard, marginBottom: K.spacing}}>Each version is represented as a JSON object with:</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing, marginBottom: K.spacing / 2}}>• <Text style={{fontWeight: 'bold', fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>created</Text> → timestamp when this version was defined.</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing, marginBottom: K.spacing}}>• <Text style={{fontWeight: 'bold', fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>expressions</Text> → an array of operations (the actual pricing formula).</Text>

                    <Text style={{...K.fonts.label, marginTop: K.spacing, marginBottom: K.spacing}}>Expressions</Text>
                    <Text style={{...K.fonts.standard, marginBottom: K.spacing}}>Expressions are the <Text style={{fontWeight: 'bold'}}>operations</Text> used to calculate pricing.</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing, marginBottom: K.spacing / 2}}>• The <Text style={{fontWeight: 'bold'}}>order of operations</Text> is important. Use <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>"group"</Text> objects to organize operations, similar to parentheses in math.</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing, marginBottom: K.spacing / 2}}>• An <Text style={{fontWeight: 'bold'}}>operator</Text> is a standard mathematical symbol: <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>+</Text>, <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>-</Text>, <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>*</Text>, <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>/</Text>, <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>%</Text>.</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing, marginBottom: K.spacing / 2}}>• Each <Text style={{fontWeight: 'bold'}}>type of expression</Text> has its own properties (see below). Always reference existing examples before creating new ones.</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing, marginBottom: K.spacing / 2}}>• <Text style={{fontWeight: 'bold'}}>Dollar values</Text> are always entered as whole numbers without decimals.</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing * 2, marginBottom: K.spacing}}>Example: <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>$43.70</Text> should be written as <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>4370</Text>.</Text>

                    <Text style={{...K.fonts.label, marginTop: K.spacing, marginBottom: K.spacing}}>Expression Object Structure</Text>
                    <Text style={{...K.fonts.standard, marginBottom: K.spacing}}>Each expression is a JSON object with these keys:</Text>

                    <Text style={{...K.fonts.standard, fontWeight: 'bold', marginBottom: K.spacing / 2}}>1. <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>type</Text></Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing, marginBottom: K.spacing / 2}}>Defines what the expression does. Examples:</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing * 2, marginBottom: K.spacing / 2}}>• <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>"fixed"</Text> → a hardcoded number.</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing * 2, marginBottom: K.spacing / 2}}>• <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>"productPropertyPrice"</Text> → fetches a price tied to a product property.</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing * 2, marginBottom: K.spacing / 2}}>• <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>"productPropertyClassifierNumber"</Text> → pulls a numeric value from a property option.</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing * 2, marginBottom: K.spacing / 2}}>• <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>"propertyValue"</Text> → gets a property value for conditional checks.</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing * 2, marginBottom: K.spacing}}>• <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>"group"</Text> → contains nested expressions.</Text>

                    <Text style={{...K.fonts.standard, fontWeight: 'bold', marginBottom: K.spacing / 2}}>2. <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>props</Text></Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing, marginBottom: K.spacing / 2}}>Configures the type. Examples:</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing * 2, marginBottom: K.spacing / 2}}>• <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>productPropertyId</Text> → which property to reference.</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing * 2, marginBottom: K.spacing / 2}}>• <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>productOptionClassifierGroupId</Text> + <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>productOptionClassifierNumberIndex</Text> → how to pull a classifier value.</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing * 2, marginBottom: K.spacing}}>• For <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>"fixed"</Text>, just <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>{'{ value: 123 }'}</Text>.</Text>

                    <Text style={{...K.fonts.standard, fontWeight: 'bold', marginBottom: K.spacing / 2}}>3. <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>operator</Text></Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing, marginBottom: K.spacing / 2}}>How this expression interacts with others. Examples:</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing * 2, marginBottom: K.spacing / 2}}>• Math: <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>+</Text>, <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>-</Text>, <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>*</Text>, <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>/</Text>, <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>%</Text></Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing * 2, marginBottom: K.spacing}}>• Logic: <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>===</Text>, <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>and</Text>, <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>or</Text></Text>

                    <Text style={{...K.fonts.standard, fontWeight: 'bold', marginBottom: K.spacing / 2}}>4. <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>description</Text> <Text style={{fontStyle: 'italic'}}>(optional but recommended)</Text></Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing, marginBottom: K.spacing}}>A human-readable explanation of what the expression does.</Text>

                    <Text style={{...K.fonts.standard, fontWeight: 'bold', marginBottom: K.spacing / 2}}>5. <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>condition</Text> <Text style={{fontStyle: 'italic'}}>(optional)</Text></Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing, marginBottom: K.spacing / 2}}>Used when the expression should only apply if certain criteria are met.</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing * 2, marginBottom: K.spacing / 2}}>• <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>condition.operator</Text> → logical operator (<Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>and</Text>, <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>or</Text>).</Text>
                    <Text style={{...K.fonts.standard, marginLeft: K.spacing * 2, marginBottom: K.spacing}}>• <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>condition.conditions</Text> → an array of checks comparing <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>actual</Text> vs. <Text style={{fontFamily: 'monospace', backgroundColor: '#f5f5f5'}}>expected</Text>.</Text>
                  </ScrollView>
                </Popup>
              )}
            </View>
          </View>
        </DocumentTitle>
      );
    }
  }
}

export default connect({
  mapState: (state, ownProps) => {
    var productPricingRuleId = ownProps.match.params.productPricingRuleId;

    return {
      productPricingRuleId,
      productPricingRule: _.get(state, `resources.productPricingRules.byId[${productPricingRuleId}]`)
    };
  },
  mapDispatch: {
    ..._.pick(resourceActions.productPricingRules, ['updateProductPricingRule', 'destroyProductPricingRule', 'trackProductPricingRules'])
  }
})(AdminProductPricingRulePage);
